/**
 * Usage credits on the Billing page (phase C5).
 *
 * **Why this is on the charge-facing page and not the budget dashboard.** With
 * overage dropped, credits are the only way a tenant continues past its
 * included allowance — buying them IS a charge, and the balance is money the
 * customer has already paid. Per-member spend still belongs on the governance
 * dashboard; this is "what did I buy and what is left".
 *
 * Four things live here, in the order an admin needs them:
 *   1. the balance, with its low-balance state
 *   2. buy — catalog packs and a custom amount
 *   3. settings — the hard-stop toggle and the low-balance threshold
 *   4. history — the ledger, which is the receipt for all of the above
 *
 * A plain MEMBER sees only (1) and (4), read-only. That is deliberate: the C4
 * exhaustion copy tells them "contact an admin", and a member who can see the
 * balance is $0 can say something useful instead of just reporting a 402.
 *
 * ── The one behaviour worth reading before editing ─────────────────────
 *
 * The balance moves on the WEBHOOK, never on the return from Stripe. A
 * customer who lands back here has paid and may still see the old figure —
 * which reads as a failed payment, and the natural response is to pay again.
 * So the balance is stashed before the redirect and polled on return until it
 * rises (`CreditWait`). Removing that reintroduces a double-charge trap.
 */
import { useCallback, useEffect, useState } from 'react'
import {
  AlertTriangle, Ban, ChevronDown, ChevronUp, Loader2, Plus, ShieldCheck,
} from 'lucide-react'
import { useConfirm, useToast } from '@papan/core-ui/ui'
import { useTranslation } from '@papan/i18n'
import type { CreditPackView, CreditsResponse } from '../client/index.js'
import {
  stashCreditBalance,
  useBuyCredits,
  useCredits,
  useUpdateCreditSettings,
  type CreditWait,
} from '../client/queries.js'
import { BILLING_MICRO_PER_MAJOR, currencyMinorUnitsPerMajor, currencySymbol, currencyDateLocale } from '@papan/types'
import { formatBillingMicro, formatCurrencyMinor, parseMajorToMinor } from './money.js'

/**
 * The deployment's charged currency, with a rolling-deploy fallback.
 *
 * An API instance that predates migration 0147 sends no `currency`, and every
 * row it has is MYR by definition — so falling back to 'myr' renders the same
 * thing that shipped before, rather than an unlabeled number or a crash inside
 * Intl. Applied at every read rather than once, because a pack carries its own
 * currency and must not silently inherit the deployment default.
 */
const FALLBACK_CURRENCY = 'myr'
import { CARD, NUMERIC } from './styles.js'

/**
 * Custom top-up amount — HIDDEN for now (may return). Only packs are offered.
 * The handler and its state are kept behind this flag rather than deleted so
 * reintroducing it is flipping one const, not rebuilding the field, its parser
 * and its bounds copy.
 */
const SHOW_CUSTOM_AMOUNT = false

interface Props {
  /** Owner/admin gate, ANDed with `writable` by the caller — matches the
   *  server's own gate so the UI never offers an action that will 403 or 404. */
  canAct: boolean
  /** Set on return from a successful credit checkout; polls until the webhook
   *  lands. Null the rest of the time, so an idle page makes no traffic. */
  awaiting: CreditWait | null
  /** Called once the awaited top-up appears, so the page can stop polling. */
  onSettled: () => void
}

/** History rows fetched initially, and the step for "show more". The server
 *  caps at 200; stopping there keeps a long-lived tenant's ledger from
 *  becoming an unbounded read on a settings page. */
const HISTORY_PAGE = 10
const HISTORY_MAX = 200

/** Ledger row tone. `topup` is money in and reads positive; `consume` is the
 *  ordinary drain and stays neutral rather than alarming; `refund` is money
 *  leaving the balance and is called out. */
function amountTone(type: string, amount: number): string {
  if (amount > 0) return 'text-emerald-400'
  if (type === 'refund') return 'text-amber-400'
  return 'themed-text-secondary'
}

export function CreditsSection({ canAct, awaiting, onSettled }: Props) {
  const { t } = useTranslation('billing')
  const toast = useToast()
  const confirm = useConfirm()

  const [historyLimit, setHistoryLimit] = useState(HISTORY_PAGE)
  const [customAmount, setCustomAmount] = useState('')
  /** Which pack button is mid-redirect ('custom' for the free-text field). */
  const [buying, setBuying] = useState<string | null>(null)
  /** The card is collapsed to just the balance until asked to expand — buy,
   *  settings and history live behind it. */
  const [expanded, setExpanded] = useState(false)

  const creditsQuery = useCredits(historyLimit, awaiting)
  const buyMut = useBuyCredits()
  const settingsMut = useUpdateCreditSettings()

  const data: CreditsResponse | undefined = creditsQuery.data

  // The awaited top-up landed — tell the page to stop polling.
  useEffect(() => {
    if (!awaiting || !data) return
    if (data.balanceBillingMicro > awaiting.atLeast) onSettled()
  }, [awaiting, data, onSettled])

  /**
   * Hand off to Stripe, remembering the balance first.
   *
   * The stash has to happen BEFORE the request: once `location.assign` runs the
   * page is gone, and the return trip has no other way to know what "changed"
   * would look like.
   */
  const buy = useCallback(
    async (purchase: { packId: string } | { customAmountMinor: number }, key: string) => {
      if (!data) return
      setBuying(key)
      try {
        stashCreditBalance(data.balanceBillingMicro)
        const { url } = await buyMut.mutateAsync(purchase)
        window.location.assign(url)
      } catch (err) {
        setBuying(null)
        toast.error(err instanceof Error ? err.message : t('errors.generic'))
      }
    },
    [data, buyMut, toast, t],
  )

  const buyCustom = useCallback(() => {
    if (!data) return
    const currency = data.currency ?? FALLBACK_CURRENCY
    // Parsed AGAINST THE CURRENCY: "1000" means ¥1,000 on a JPY deployment and
    // RM10.00 on a MYR one. A fixed ×100 here would have been a 100× error.
    const minor = parseMajorToMinor(customAmount, currency)
    // Client-side bounds are for the message, not the guard — the route
    // re-validates against the same numbers. Saying WHICH bound was missed
    // beats a generic rejection after a round trip.
    if (minor === null) {
      // The example follows the CURRENCY's own exponent. This message used to
      // read "Enter an amount in ringgit, e.g. 100 or 99.50" in all four
      // locales — a currency name hardcoded into a whitelabel product, and an
      // example that is invalid input on a zero-decimal deployment, where
      // `parseMajorToMinor` rejects the decimals it was telling people to type.
      // The field already shows the currency symbol, so the word is redundant
      // as well as wrong.
      toast.error(t('credits.buy.invalidAmount', {
        example: currencyMinorUnitsPerMajor(currency) === 1 ? '1000' : '100, 99.50',
      }))
      return
    }
    if (minor < data.bounds.floorMinor) {
      toast.error(t('credits.buy.belowFloor', { min: formatCurrencyMinor(data.bounds.floorMinor, currency) }))
      return
    }
    if (minor > data.bounds.ceilingMinor) {
      toast.error(t('credits.buy.aboveCeiling', { max: formatCurrencyMinor(data.bounds.ceilingMinor, currency) }))
      return
    }
    void buy({ customAmountMinor: minor }, 'custom')
  }, [customAmount, data, buy, toast, t])

  const toggleEnabled = useCallback(async () => {
    if (!data) return
    const next = !data.enabled
    // Only the OFF direction is confirmed. Turning credits off stops AI for
    // the whole org the moment the included budget is gone — that is a
    // governance decision, not a preference toggle. Turning them back on
    // restores the default and needs no ceremony.
    if (!next) {
      const ok = await confirm({
        title: t('credits.settings.disableTitle'),
        message: t('credits.settings.disableMessage', {
          balance: formatBillingMicro(data.balanceBillingMicro, data.currency ?? FALLBACK_CURRENCY),
        }),
        confirmText: t('credits.settings.disableConfirm'),
        type: 'danger',
      })
      if (!ok) return
    }
    try {
      await settingsMut.mutateAsync({ enabled: next })
      toast.success(next ? t('credits.settings.enabled') : t('credits.settings.disabled'))
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('errors.generic'))
    }
  }, [data, confirm, settingsMut, toast, t])

  // A member's read is allowed, so a failure here is a real error rather than
  // the silent 403 that /invoices and /payment-method absorb. Still non-fatal:
  // the rest of the billing page is unaffected, so the section just says so.
  if (creditsQuery.isPending) {
    return (
      <div className={`${CARD} p-6 flex items-center gap-2 themed-text-muted text-sm`}>
        <Loader2 size={14} className="animate-spin" />
        {t('credits.loading')}
      </div>
    )
  }
  if (creditsQuery.error || !data) return null

  const threshold = data.effectiveLowBalanceThresholdBillingMicro
  const low = threshold !== null && data.balanceBillingMicro <= threshold
  const empty = data.balanceBillingMicro <= 0
  const off = !data.enabled
  const busy = buying !== null || settingsMut.isPending
  // How many minor units make one major unit in the deployment's currency —
  // 100 for MYR/USD, 1 for JPY. Drives the placeholder and the keypad hint;
  // the actual parse goes through `parseMajorToMinor`, which applies the same
  // exponent server-side too.
  const minorPerMajor = currencyMinorUnitsPerMajor(data.currency ?? FALLBACK_CURRENCY)

  // The unit EVERY figure below is denominated in — the balance and the
  // threshold in billing micro, the pack prices in minor units. Resolved once
  // here rather than per call site: a screen that derived it twice is how one
  // amount comes to wear a symbol the rest of the page disagrees with.
  const currency = data.currency ?? FALLBACK_CURRENCY

  return (
    <section className="space-y-3">
      {/* No section heading here: the card carries its own "Credit balance"
          label, so a "Usage credits" eyebrow floating above it was a second,
          card-less title for the same thing (removed 2026-08-19). */}
      <div className={`${CARD} p-4 sm:p-6 relative ${expanded ? 'space-y-5' : ''}`}>
        {/* ONE toggle, fixed at the same corner in both states — so expanding
            or collapsing never moves the control OR the balance beside it. The
            old layout put "Manage" inline-right when collapsed and "Collapse"
            absolute-top-right when expanded, so both jumped on toggle. */}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="absolute top-4 right-4 sm:top-6 sm:right-6 z-20 flex items-center gap-1 text-xs font-medium text-[color:var(--bill-primary)] hover:underline"
        >
          {expanded ? t('credits.collapse', { defaultValue: 'Collapse' }) : t('credits.manage', { defaultValue: 'Manage' })}
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>

        {/* Collapsed: only the balance. The "Manage" toggle above is the single
            expand affordance (the whole card is no longer a second button — one
            control, and it stays put across the toggle). `pr-24` reserves the
            corner the toggle occupies so the balance never sits under it. */}
        {!expanded ? (
          <div className="min-w-0 pr-24">
            <p className="text-[11px] themed-text-muted uppercase tracking-wide">{t('credits.balance')}</p>
            <p className={`text-xl sm:text-2xl themed-text font-semibold tracking-tight ${NUMERIC}`}>
              {formatBillingMicro(data.balanceBillingMicro, currency)}
            </p>
            {off && <p className="text-xs text-amber-400 mt-0.5">{t('credits.off.title')}</p>}
          </div>
        ) : (
        <>
        {/* ── Balance + buy ─────────────────────────────────────────
            Veiled as ONE region while the hard stop is on. The state is
            structural, not a footnote: with credits off, every figure and
            control in here describes money that will not be spent, so a badge
            next to a live-looking "Buy" grid understates it. The controls that
            change the state (settings) and the receipt (history) stay clear
            below — an admin still has to be able to undo this and read what
            was already bought. */}
        <div className={`relative ${off ? 'min-h-36' : ''}`}>
          <div
            // The blur is a FILTER on the content rather than a backdrop-filter
            // on the overlay: backdrop-blur silently no-ops when an ancestor
            // already owns one, and this card sits wherever the settings pane
            // puts it. `inert` takes the veiled controls out of the tab order
            // too — a blurred Buy button that is still keyboard-reachable would
            // send someone to Stripe from a state that says "off".
            inert={off}
            className={`space-y-5 transition duration-200 ${
              off ? 'blur-[3px] opacity-40 select-none' : ''
            }`}
          >
            <div className="min-w-0 space-y-1 pr-24">
              <p className="text-[11px] themed-text-muted uppercase tracking-wide">
                {t('credits.balance')}
              </p>
              <p className={`text-xl sm:text-2xl themed-text font-semibold tracking-tight ${NUMERIC}`}>
                {formatBillingMicro(data.balanceBillingMicro, currency)}
              </p>
              {/* Credits never expire — worth stating, because every other
                  allowance on this page does. */}
              <p className="text-xs themed-text-muted">{t('credits.neverExpires')}</p>
            </div>

            {/* Low balance / empty. Two distinct statements: "running low" is a
                heads-up, "used up" is a stop that has already happened. */}
            {(low || empty) && (
              <div
                className={`flex items-start gap-2 text-xs rounded-lg px-3 py-2 ring-1 ${
                  empty
                    ? 'text-red-400 bg-red-500/8 ring-red-500/20'
                    : 'text-amber-400 bg-amber-500/8 ring-amber-500/20'
                }`}
              >
                <AlertTriangle size={14} className="shrink-0 mt-px" />
                <span>
                  {empty
                    ? t('credits.emptyNote')
                    : t('credits.lowNote', { threshold: formatBillingMicro(threshold ?? 0, currency) })}
                </span>
              </div>
            )}

            {/* ── Buy ───────────────────────────────────────────────── */}
            {canAct && (
              <div className="pt-5 border-t themed-border space-y-3">
                <p className="text-sm themed-text-secondary font-medium">{t('credits.buy.heading')}</p>

                {data.packs.length > 0 ? (
                  <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                    {data.packs.map((p: CreditPackView) => (
                      <li key={p.id}>
                        <button
                          onClick={() => void buy({ packId: p.id }, p.id)}
                          disabled={!p.purchasable || busy}
                          // A pack with no Stripe price is shown DISABLED with a
                          // reason rather than hidden: hiding it looks like a
                          // catalog change, disabling it looks like the deployment
                          // gap it actually is.
                          title={p.purchasable ? undefined : t('credits.buy.unsynced')}
                          // Reads as a price card, not a chip: the figures are
                          // left-aligned on a common edge so the column scans
                          // vertically, and the MYR price carries the weight
                          // because it is the only number the customer is choosing
                          // between. `min-h` keeps the tile from collapsing when
                          // the two lines are swapped for the spinner.
                          className={`w-full px-3 py-2 min-h-[5.25rem] rounded-lg flex flex-col items-start justify-start leading-tight text-left transition-all active:scale-[0.98] disabled:active:scale-100 ${
                            p.purchasable
                              ? 'ring-1 ring-[color:var(--bill-ring)] text-[color:var(--bill-primary)] hover:bg-[color:var(--bill-tint)] hover:ring-[color:var(--bill-primary)]'
                              : 'themed-bg-tertiary themed-text-muted cursor-not-allowed'
                          }`}
                        >
                          {buying === p.id ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <>
                              <span className={`text-lg font-bold tracking-tight ${NUMERIC}`}>
                                {formatCurrencyMinor(p.priceMinor, p.currency ?? data.currency ?? FALLBACK_CURRENCY)}
                              </span>
                              {/* Both units, because they are different things:
                                  the charged currency is what the card pays,
                                  USD is what gets metered away. */}
                              <span className={`text-[11px] font-medium themed-text-muted ${NUMERIC}`}>
                                {formatBillingMicro(p.creditBillingMicro, p.currency ?? currency)}
                              </span>
                            </>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs themed-text-muted">{t('credits.buy.noPacks')}</p>
                )}

                {/* Custom amount. Bounds are stated up front rather than only on
                    rejection — a customer typing RM5 should learn the floor before
                    they press the button, not after. HIDDEN for now (packs only);
                    see SHOW_CUSTOM_AMOUNT. */}
                {SHOW_CUSTOM_AMOUNT && (<>
                <div className="flex items-end gap-2 flex-wrap">
                  <label className="flex-1 min-w-40">
                    <span className="block text-[11px] themed-text-muted uppercase tracking-wide mb-1">
                      {t('credits.buy.customLabel')}
                    </span>
                    <div className="flex items-center gap-1.5">
                      {/* The currency's own symbol, derived rather than the
                          literal "RM" this used to be. `currencySymbol` falls
                          back to the ISO code when a runtime has no symbol for
                          it, which is a correct label either way. */}
                      <span className="text-sm themed-text-muted">
                        {currencySymbol(data.currency ?? FALLBACK_CURRENCY)}
                      </span>
                      <input
                        value={customAmount}
                        onChange={(e) => setCustomAmount(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') buyCustom() }}
                        // `decimal` on a zero-decimal currency invites input the
                        // parser will reject; ask for the right keypad.
                        inputMode={minorPerMajor === 1 ? 'numeric' : 'decimal'}
                        // The floor in MAJOR units — divided by the currency's
                        // own factor, not a hardcoded 100.
                        placeholder={String(Math.round(data.bounds.floorMinor / minorPerMajor))}
                        className={`themed-input flex-1 px-3 py-2 rounded-lg text-sm ${NUMERIC}`}
                      />
                    </div>
                  </label>
                  <button
                    onClick={buyCustom}
                    disabled={busy || customAmount.trim() === ''}
                    className="px-4 py-2 rounded-lg text-sm font-medium themed-btn-primary flex items-center gap-1.5 disabled:opacity-60"
                  >
                    {buying === 'custom' ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                    {t('credits.buy.action')}
                  </button>
                </div>
                <p className="text-[11px] themed-text-muted">
                  {t('credits.buy.bounds', {
                    min: formatCurrencyMinor(data.bounds.floorMinor, data.currency ?? FALLBACK_CURRENCY),
                    max: formatCurrencyMinor(data.bounds.ceilingMinor, data.currency ?? FALLBACK_CURRENCY),
                  })}
                </p>
                </>)}
              </div>
            )}
          </div>

          {off && (
            <CreditsOffOverlay
              canAct={canAct}
              busy={settingsMut.isPending}
              onEnable={() => void toggleEnabled()}
            />
          )}
        </div>

        {/* ── Settings ──────────────────────────────────────────── */}
        {canAct && (
          <div className="pt-5 border-t themed-border space-y-3">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <p className="text-sm themed-text-secondary font-medium flex items-center gap-1.5">
                  <ShieldCheck size={14} className="themed-text-muted" />
                  {t('credits.settings.heading')}
                </p>
                <p className="text-xs themed-text-muted mt-0.5">
                  {t('credits.settings.hint')}
                </p>
              </div>
              <button
                onClick={() => void toggleEnabled()}
                disabled={settingsMut.isPending}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-60 ${
                  data.enabled
                    ? 'ring-1 ring-red-500/30 text-red-400 hover:bg-red-500/8'
                    : 'themed-btn-primary'
                }`}
              >
                {settingsMut.isPending
                  ? <Loader2 size={12} className="animate-spin" />
                  : data.enabled ? t('credits.settings.turnOff') : t('credits.settings.turnOn')}
              </button>
            </div>

            <ThresholdRow data={data} />
          </div>
        )}

        {/* ── History ───────────────────────────────────────────── */}
        <div className="pt-5 border-t themed-border space-y-3">
          <p className="text-sm themed-text-secondary font-medium">{t('credits.history.heading')}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="text-[10px] themed-text-muted uppercase tracking-[0.08em] border-b themed-border">
                  <th className="font-semibold py-2 pr-4">{t('credits.history.col.date')}</th>
                  <th className="font-semibold py-2 pr-4">{t('credits.history.col.type')}</th>
                  <th className="font-semibold py-2 pr-4 text-right">{t('credits.history.col.amount')}</th>
                  <th className="font-semibold py-2 text-right">{t('credits.history.col.balance')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[color:var(--border-primary)]">
                {data.transactions.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="py-6 text-sm themed-text-muted text-center">
                      {t('credits.history.empty')}
                    </td>
                  </tr>
                ) : (
                  data.transactions.map((tx) => (
                    <tr key={tx.id}>
                      <td className={`py-2 pr-4 text-xs themed-text-muted ${NUMERIC}`}>
                        {formatTxDate(tx.createdAt, data.currency ?? FALLBACK_CURRENCY)}
                      </td>
                      <td className="py-2 pr-4 text-xs themed-text-secondary">
                        {t(`credits.history.type.${tx.type}`, tx.type)}
                      </td>
                      {/* The sign is explicit on the way IN: "+$18.18" and
                          "−$0.42" are legible as directions at a glance, where
                          a bare figure needs the type column to disambiguate. */}
                      <td className={`py-2 pr-4 text-xs text-right font-medium ${NUMERIC} ${amountTone(tx.type, tx.amountBillingMicro)}`}>
                        {tx.amountBillingMicro > 0 ? '+' : ''}
                        {formatBillingMicro(tx.amountBillingMicro, tx.billingCurrency ?? currency)}
                      </td>
                      <td className={`py-2 text-xs text-right themed-text ${NUMERIC}`}>
                        {formatBillingMicro(tx.balanceAfterBillingMicro, tx.billingCurrency ?? currency)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {/* A full page came back, so there may be more. Stops at the server's
              own cap rather than offering a button that returns the same rows. */}
          {data.transactions.length >= historyLimit && historyLimit < HISTORY_MAX && (
            <button
              onClick={() => setHistoryLimit((n) => Math.min(n + HISTORY_PAGE, HISTORY_MAX))}
              disabled={creditsQuery.isFetching}
              className="text-xs font-medium text-[color:var(--bill-primary)] hover:underline disabled:opacity-60"
            >
              {creditsQuery.isFetching ? t('credits.history.loading') : t('credits.history.more')}
            </button>
          )}
        </div>
        </>
        )}
      </div>
    </section>
  )
}

/**
 * What sits on top of the veiled balance/buy region while the hard stop is on.
 *
 * It says the thing the old chip only labelled: the balance below is real money
 * that will NOT be spent. The wash uses `--bg-secondary` — the card's own
 * surface — at partial opacity rather than a black scrim, so it reads as
 * "darkened" on the dark themes and "washed out" on light, instead of a grey
 * slab that only one theme was designed for.
 *
 * A member gets the same statement without a button: the server gates the
 * toggle on billing management, so offering it here would be an action that
 * 403s. They are told an admin can undo it, which is what they can act on.
 */
function CreditsOffOverlay(
  { canAct, busy, onEnable }: { canAct: boolean; busy: boolean; onEnable: () => void },
) {
  const { t } = useTranslation('billing')
  return (
    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 px-4 text-center rounded-xl bg-[color:var(--bg-secondary)]/60">
      <span className="flex items-center justify-center w-9 h-9 rounded-full bg-amber-500/12 text-amber-400 ring-1 ring-amber-500/25">
        <Ban size={16} />
      </span>
      <p className="text-sm font-semibold themed-text">{t('credits.off.title')}</p>
      <p className="text-xs themed-text-muted max-w-sm">
        {canAct ? t('credits.off.body') : t('credits.off.bodyMember')}
      </p>
      {canAct && (
        <button
          onClick={onEnable}
          disabled={busy}
          className="mt-1 px-3.5 py-1.5 rounded-lg text-xs font-medium themed-btn-primary flex items-center gap-1.5 disabled:opacity-60"
        >
          {busy && <Loader2 size={12} className="animate-spin" />}
          {t('credits.off.action')}
        </button>
      )}
    </div>
  )
}

/**
 * The low-balance threshold editor.
 *
 * Shows the EFFECTIVE figure, not the stored column. The column is null for
 * almost every tenant — rendering it would leave an empty field that reads as
 * "no warning is armed" when in fact one is (20% of the last top-up). The
 * three sources get three different hints, because `none` genuinely means
 * nothing will warn.
 */
function ThresholdRow({ data }: { data: CreditsResponse }) {
  const { t } = useTranslation('billing')
  const toast = useToast()
  const settingsMut = useUpdateCreditSettings()

  const effective = data.effectiveLowBalanceThresholdBillingMicro
  const currency = data.currency ?? FALLBACK_CURRENCY
  const [draft, setDraft] = useState('')
  const [editing, setEditing] = useState(false)

  const save = async (value: number | null) => {
    try {
      await settingsMut.mutateAsync({ lowBalanceThresholdBillingMicro: value })
      setEditing(false)
      toast.success(t('credits.settings.thresholdSaved'))
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('errors.generic'))
    }
  }

  const submit = () => {
    // Parsed against the DEPLOYMENT'S CURRENCY, which is the reverse of what
    // this used to do — and the reversal is the P8/P9 flip, not a change of
    // mind. The threshold is a BALANCE figure, and the balance stopped being
    // USD microdollars: it is billing micro, the unit the customer bought in.
    // The old comment's reasoning ("the balance is metered in USD everywhere")
    // was correct for its time and is now exactly backwards; parsing as USD
    // here would mis-scale the field on every zero- and three-decimal
    // deployment.
    const minor = parseMajorToMinor(draft, currency)
    if (minor === null) {
      toast.error(t('credits.settings.thresholdInvalid'))
      return
    }
    // Minor → billing micro through the currency's own exponent, never a
    // hardcoded ×10_000.
    void save(minor * (BILLING_MICRO_PER_MAJOR / currencyMinorUnitsPerMajor(currency)))
  }

  if (editing) {
    return (
      <div className="flex items-end gap-2 flex-wrap">
        <label className="flex-1 min-w-40">
          <span className="block text-[11px] themed-text-muted uppercase tracking-wide mb-1">
            {t('credits.settings.thresholdLabel')}
          </span>
          <div className="flex items-center gap-1.5">
            <span className="text-sm themed-text-muted">$</span>
            <input
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submit() }}
              inputMode="decimal"
              className={`themed-input flex-1 px-3 py-2 rounded-lg text-sm ${NUMERIC}`}
            />
          </div>
        </label>
        <button
          onClick={submit}
          disabled={settingsMut.isPending}
          className="px-3 py-2 rounded-lg text-xs font-medium themed-btn-primary disabled:opacity-60"
        >
          {settingsMut.isPending ? <Loader2 size={12} className="animate-spin" /> : t('credits.settings.thresholdSave')}
        </button>
        <button
          onClick={() => setEditing(false)}
          className="px-3 py-2 rounded-lg text-xs font-medium themed-btn-secondary"
        >
          {t('credits.settings.thresholdCancel')}
        </button>
        {/* Only offered once an explicit value exists — "reset" on a tenant
            that never set one would silently mean "keep the default". */}
        {data.lowBalanceThresholdBillingMicro !== null && (
          <button
            onClick={() => void save(null)}
            disabled={settingsMut.isPending}
            className="px-3 py-2 rounded-lg text-xs font-medium themed-text-muted hover:themed-text disabled:opacity-60"
          >
            {t('credits.settings.thresholdReset')}
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <div className="min-w-0">
        <p className="text-xs themed-text-secondary">
          {t('credits.settings.thresholdLabel')}:{' '}
          <span className={`themed-text font-medium ${NUMERIC}`}>
            {effective === null ? t('credits.settings.thresholdNone') : formatBillingMicro(effective, currency)}
          </span>
        </p>
        <p className="text-[11px] themed-text-muted mt-0.5">
          {data.lowBalanceThresholdSource === 'explicit'
            ? t('credits.settings.thresholdExplicit')
            : data.lowBalanceThresholdSource === 'default_last_topup'
              ? t('credits.settings.thresholdDefault', {
                  pct: Math.round(data.lowBalanceDefaultFraction * 100),
                })
              : t('credits.settings.thresholdNoneHint')}
        </p>
      </div>
      <button
        onClick={() => {
          setDraft(effective === null ? '' : (effective / 1_000_000).toFixed(2))
          setEditing(true)
        }}
        className="text-xs font-medium text-[color:var(--bill-primary)] hover:underline shrink-0"
      >
        {t('credits.settings.thresholdEdit')}
      </button>
    </div>
  )
}

/** ISO → short date+time in the display currency's locale (so the date order
 *  matches the amounts), kept in LOCAL time. The ledger's ordering only makes
 *  sense with the time: several rows can land in one minute during an active
 *  session. */
function formatTxDate(iso: string, currency: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString(currencyDateLocale(currency), {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}
